import Proptypes from "prop-types";
import { Button, LinearProgress, Chip, Tooltip } from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import { useContext, useState, useMemo } from "react";
import { SignatureContext } from "../../../context/SignatureContext";
import { FddApi } from "../../../../../ui/services/server-fdd";
import { useNavigate, useLocation } from "react-router-dom";
import { getIdFromPath } from "../../../../../ui/utils/getIdFromPath";
import { TYPES } from "../../../../../config/constants/typesReducers";
import { useNotification } from "../../../../../ui/context/notification/NotificationContext";

export const FormMobile = ({
    handleBeforeField,
    handleNextField,
    currentField,
    sortedFields,
    indexSortedField,
}) => {
    const { signatureDispatch, signatureState } = useContext(SignatureContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { showNotification } = useNotification();

    const sortedFieldsArray = useMemo(() => Object.values(sortedFields || {}), [sortedFields]);
    const totalFields = sortedFieldsArray.length;
    const filledCount = useMemo(() =>
        sortedFieldsArray.filter(f => !f.isEmpty).length,
    [sortedFieldsArray]);
    const allFieldsFilled = filledCount === totalFields;
    const progress = totalFields > 0 ? (filledCount / totalFields) * 100 : 0;
    const isLastField = indexSortedField === totalFields - 1;

    const handleFinish = async () => {
        setIsSubmitting(true);
        try {
            const signatureId = getIdFromPath(location.pathname);

            if (currentField && currentField.field && !currentField.isEmpty) {
                signatureDispatch({
                    type: TYPES.SIGNATURE.EDIT_FIELDS,
                    payload: { field: currentField.field, value: currentField.value }
                });
            }

            const response = await FddApi.completeSignature(signatureId, signatureState.signature.fieldsByPage);
            
            if (response.hasError) throw new Error(response.message);

            showNotification('¡Firma completada exitosamente!', 'success', 5000);
            navigate('/');
        } catch (error) {
            console.error('[FINISH SIGNATURE] Error:', error);
            showNotification('Error al completar la firma: ' + error.message, 'error', 6000);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className='form-mobile'>
            {/* Progreso */}
            <div style={{ padding: '0 4px 8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '0.75rem', color: '#666' }}>
                        {filledCount} de {totalFields} campos
                    </span>
                    {allFieldsFilled && (
                        <Chip
                            icon={<CheckCircleOutline style={{ fontSize: 14 }} />}
                            label="Listo"
                            color="success"
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                    )}
                </div>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    color={allFieldsFilled ? 'success' : 'primary'}
                    sx={{ borderRadius: 4, height: 5 }}
                />
            </div>

            {/* Navegación entre campos */}
            <div className="form-mobile__buttons">
                <Button
                    onClick={handleBeforeField}
                    variant="text"
                    disableElevation
                    style={{ visibility: indexSortedField > 0 ? 'visible' : 'hidden' }}
                    color='secondary'>
                    Anterior
                </Button>
                <p className='sm-text grey-text'>{indexSortedField + 1} / {totalFields}</p>
                <Button
                    onClick={handleNextField}
                    variant="text"
                    disabled={currentField?.isEmpty || isLastField}
                    disableElevation
                    color='primary'
                    sx={{ touchAction: 'manipulation' }}>
                    Siguiente
                </Button>
            </div>

            {/* Botón Finalizar - siempre visible */}
            <Tooltip
                title={!allFieldsFilled ? `Faltan ${totalFields - filledCount} campo(s) por completar` : ''}
                arrow>
                <span style={{ display: 'block', padding: '0 4px' }}>
                    <Button
                        onClick={handleFinish}
                        variant="contained"
                        disabled={!allFieldsFilled || isSubmitting}
                        disableElevation
                        color='primary'
                        fullWidth
                        size="large"
                        sx={{
                            borderRadius: 2,
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            touchAction: 'manipulation'
                        }}>
                        {isSubmitting ? 'Enviando...' : '✓ Finalizar Firma'}
                    </Button>
                </span>
            </Tooltip>
        </div>
    )
}

FormMobile.propTypes = {
    handleBeforeField: Proptypes.func,
    handleNextField: Proptypes.func,
    currentField: Proptypes.object,
    sortedFields: Proptypes.object,
    indexSortedField: Proptypes.number,
}